import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import styled from "styled-components"

import { isClientSide } from "@utils"
import { MEDIA_QUERIES } from "@constants"

import { BespokeComponentFragmentFragment, PricingTableQueryQuery } from "@graphqlTypes"

import PricingTableHeader from "./PricingTableHeader/PricingTableHeader"
import PricingTableFeatures from "./PricingTableFeatures/PricingTableFeatures"
import LpSection from "@components/lp/LpSection/LpSection"
import PricingTableStickyHeader from "./PricingTableStickyHeader/PricingTableStickyHeader"
import ContentfulLpContext from "@context/ContentfulLpContext/ContentfulLpContext"

const PricingTableSECTION = styled(LpSection)`
  display: none;
  margin-top: var(--spacing--XL);

  ${MEDIA_QUERIES.L} {
    display: block;
  }
`

type PricingTableJsonPayload = {
  title: string
}

const PricingTable: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
  const { pricingTableJson, pricingJson } = useStaticQuery<PricingTableQueryQuery>(graphql`
    fragment PricingTableFeatureFragment on contentfulJsonDataJsonJsonNodeCategoriesFeatures {
      title
      description
      planFree
      planStandard
      planPlus
      planPro
      isAnnualDiscountApplied {
        planFree
        planStandard
        planPlus
        planPro
      }
    }

    fragment PricingTablePlanFragment on contentfulJsonDataJsonJsonNodePlans {
      baseContacts
      ctaLink
      ctaText
      id
      maxContacts
      name
    }

    query PricingTableQuery {
      pricingTableJson: contentfulJsonData(
        contentful_id: {eq: "6DLqEDy1BxiGH5uG7QpvGm"}
      ) {
        json {
          categories {
            title
            features {
              ...PricingTableFeatureFragment
            }
          }
        }
      }
      pricingJson: contentfulJsonData(contentful_id: {eq: "45c4tePUA3uIu5WPPfUCez"}) {
        json {
          annualDiscountPercentage
          plans {
            ...PricingTablePlanFragment
          }
        }
      }
    }
  `)

  const json = useMemo(() => {
    try {
      return JSON.parse(jsonPayload?.internal.content || "") as PricingTableJsonPayload
    } catch (e) {
      console.error(`Failed to parse JSON Payload: PricingTable (${contentful_id})`)
      return null
    }
  }, [jsonPayload])

  if (!json || !(pricingJson?.json?.plans && pricingTableJson?.json?.categories)) return null

  const [currentCategoryName, setCurrentCategoryName] = useState<string>("")
  const [stickyHeaderIsVisible, setStickyHeaderIsVisible] = useState<boolean>(false)
  const FeaturesRef = useRef<HTMLDivElement | null>(null)
  const ObserverRef = useRef<IntersectionObserver | null>(null)
  const { isSalesPage } = useContext(ContentfulLpContext)

  const {
    annualDiscountPercentage,
    plans,
  } = pricingJson.json

  const { categories: features } = pricingTableJson.json

  useEffect(() => {
    if (!isClientSide() || !FeaturesRef.current) return

    const handleObserver: IntersectionObserverCallback = (entries) => {
      if (entries[0].isIntersecting) {
        setStickyHeaderIsVisible(true)
      } else {
        setStickyHeaderIsVisible(false)
      }
    }

    ObserverRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0,
      rootMargin: "0px 0px -95%",
    })

    ObserverRef.current.observe(FeaturesRef.current)
  }, [])

  if (!plans || !features) return null

  const plansToUse = isSalesPage ? plans.slice(1, 4) : plans.slice(0, 3)

  return (
    <PricingTableSECTION>
      <PricingTableHeader
        // annualDiscountPercentage={annualDiscountPercentage || 0}
        plans={plansToUse}
        titleHtml={json.title}
      />
      <div ref={FeaturesRef}>
        <PricingTableStickyHeader
          isVisible={stickyHeaderIsVisible}
          plans={plansToUse}
          title={currentCategoryName}
        />
        {features.map((category) => !category ? null : (
          <PricingTableFeatures
            features={category.features}
            key={category.title}
            onIntersecting={() => setCurrentCategoryName(category.title || "")}
            plans={plansToUse}
            title={category.title}
          />
        ))}
      </div>
    </PricingTableSECTION>
  )
}

export default PricingTable
